import React, { useState, useEffect } from 'react';
import { updateSavedQuiz, getTeacherFolders } from '../../services/quizLibraryService';
import { useToast } from '../common/Toast';
import type { SavedQuiz } from '../../services/quizLibraryService';
import GradeLevelSelector from '../GradeLevelSelector';

interface EditQuizModalProps {
  isOpen: boolean;
  onClose: () => void;
  quiz: SavedQuiz | null;
  teacherId: string;
  onSaved: () => void;
}

export default function EditQuizModal({
  isOpen,
  onClose,
  quiz,
  teacherId,
  onSaved
}: EditQuizModalProps) {
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [folders, setFolders] = useState<string[]>([]);
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    subject: 'General',
    gradeLevel: 'High School (9-12)',
    examBoard: 'General Curriculum',
    topic: '',
    tags: [] as string[],
    folder: '',
    difficulty: 'Medium' as 'Easy' | 'Medium' | 'Hard' | 'Mixed',
    description: '',
    isTemplate: false
  });

  // Update form data when quiz changes
  useEffect(() => {
    if (quiz) {
      setFormData({
        name: quiz.name,
        subject: quiz.subject,
        gradeLevel: quiz.gradeLevel,
        examBoard: quiz.examBoard || 'General Curriculum',
        topic: quiz.topic,
        tags: [...quiz.tags],
        folder: quiz.folder || '',
        difficulty: quiz.difficulty,
        description: quiz.description || '',
        isTemplate: quiz.isTemplate
      });
    }
  }, [quiz]);

  // Load folders when modal opens
  useEffect(() => {
    if (isOpen && teacherId) {
      getTeacherFolders(teacherId).then(setFolders).catch(console.error);
    }
  }, [isOpen, teacherId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quiz) return;

    setIsSaving(true);
    try {
      await updateSavedQuiz(quiz.id, {
        ...formData,
        folder: showNewFolderInput ? newFolderName : formData.folder,
      });

      toast.success(`Quiz "${formData.name}" updated successfully!`);
      onSaved();
      onClose();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update quiz');
    } finally {
      setIsSaving(false);
    }
  };

  const addTag = (tag: string) => {
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tag.toLowerCase()]
      }));
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  if (!isOpen || !quiz) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              ✏️ Edit Quiz
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          <p className="text-gray-600 mt-1">
            Update quiz details and organization
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quiz Name *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter quiz name..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <select
                value={formData.subject}
                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Mathematics">Mathematics</option>
                <option value="Science">Science</option>
                <option value="Physics">Physics</option>
                <option value="Chemistry">Chemistry</option>
                <option value="Biology">Biology</option>
                <option value="History">History</option>
                <option value="English">English</option>
                <option value="Geography">Geography</option>
                <option value="General">General</option>
              </select>
            </div>
          </div>

          {/* Topic and Grade Level */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Topic
              </label>
              <input
                type="text"
                value={formData.topic}
                onChange={(e) => setFormData(prev => ({ ...prev, topic: e.target.value }))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="e.g., Fractions, Forces, World War II..."
              />
            </div>

            <div>
              <GradeLevelSelector
                value={formData.gradeLevel}
                onChange={(gradeLevel) => setFormData(prev => ({ ...prev, gradeLevel }))}
                showSystemSelector={true}
              />
            </div>
          </div>

          {/* Folder Organization */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📁 Organize in Folder (Optional)
            </label>
            <div className="space-y-2">
              {!showNewFolderInput ? (
                <div className="flex gap-2">
                  <select
                    value={formData.folder}
                    onChange={(e) => setFormData(prev => ({ ...prev, folder: e.target.value }))}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">No folder</option>
                    {folders.map(folder => (
                      <option key={folder} value={folder}>{folder}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => setShowNewFolderInput(true)}
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium"
                  >
                    + New Folder
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter folder name..."
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowNewFolderInput(false);
                      setNewFolderName('');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              🏷️ Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    className="text-blue-600 hover:text-blue-800 ml-1"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Add tags (press Enter)..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addTag((e.target as HTMLInputElement).value);
                    (e.target as HTMLInputElement).value = '';
                  }
                }}
              />
              <div className="flex gap-1">
                {['assessment', 'practice', 'homework', 'test'].map(suggestedTag => (
                  <button
                    key={suggestedTag}
                    type="button"
                    onClick={() => addTag(suggestedTag)}
                    className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200"
                  >
                    {suggestedTag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📝 Description (Optional)
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Add notes about this quiz for future reference..."
            />
          </div>

          {/* Template Option */}
          <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <input
              type="checkbox"
              id="isTemplate"
              checked={formData.isTemplate}
              onChange={(e) => setFormData(prev => ({ ...prev, isTemplate: e.target.checked }))}
              className="w-4 h-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 rounded"
            />
            <label htmlFor="isTemplate" className="text-sm font-medium text-yellow-800">
              ⭐ Template Quiz
            </label>
            <span className="text-xs text-yellow-600">
              (Templates appear in a special section for easy reuse)
            </span>
          </div>

          {/* Quiz Info Summary */}
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h4 className="font-medium text-gray-900 mb-2">Quiz Summary:</h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
              <div>📊 <strong>{quiz.totalQuestions}</strong> questions</div>
              <div>⏱️ <strong>~{quiz.estimatedTime}</strong> minutes</div>
              <div>🎯 <strong>{quiz.difficulty}</strong> difficulty</div>
              <div>🧠 <strong>{quiz.cognitiveLevel?.length || 0}</strong> cognitive levels</div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !formData.name.trim()}
              className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                <>
                  ✏️ Update Quiz
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}